<template>
    <div class="container">
        <o-bread-crumb>
            <template v-slot:pageName>Transaction</template>
            <template v-slot:pageDetail>List</template>
        </o-bread-crumb>        
        <div v-if="validation">
            <div class="alert alert-danger mt-1" role="alert">
                {{ validation }}
            </div>
        </div>
        <div v-if="thisComponentShow" class="card text-center bg-white shadow-none">
            <div class="card-header">
                <ul class="nav nav-pills card-header-pills justify-content-between ms-2 me-4 mt-2 mb-2">
                    <li class="nav-item text-start">
                        <div class="d-flex d-inline-block">
                            <h4 class="header-title">
                                <i class="fas fa-receipt me-2 text-primary"></i>List of Transactions
                            </h4>
                        </div>
                        <p class="header-subtitle me-4 mt-1">Manage and view all transactions</p>   
                    </li>
                </ul>
            </div>
            <div class="card-body">
                <div class="d-flex justify-content-center spinner-border text-primary spinner-position"
                    v-if="page.isLoading">
                </div>
                <div class="d-flex flex-column justify-content-center text-primary align-items-center spinner-position"
                    v-if="isExporting">
                    <div class="spinner-border text-primary" role="status"></div>
                    <span class="mt-2 text-primary exporting-animation">
                        <span v-for="(char, index) in 'Exporting'" :key="index" class="fade-char" :style="{ '--i': index }">
                            {{ char }}
                        </span>
                    </span>
                </div>
                <div class="spinner" v-if='page.isLoading || isExporting'></div>
                <div class="" v-if="page.output">
                    <div class="filter-action-wrapper mb-4 mt-3">
                        <div class="filter-controls">
                            <!-- Merchant Selection (Admin/Corporate only) -->
                            <div class="merchant-filter" v-if="selectedBusinessProfile.merchant_id == null">
                                <o-select v-model="selectMerchant" :options="merchants" autocomplete="'off'" placeholder="Select Merchant"></o-select>
                            </div>

                            <!-- Search Control (Tabs + Input) -->
                            <div class="search-box-custom">
                                <div class="search-type-tabs">
                                    <button 
                                        v-for="option in searchOptions" 
                                        :key="option.value"
                                        class="tab-btn"
                                        :class="{ active: searchBy.selected === option.value }"
                                        @click="selectSearchType(option.value)"
                                    >
                                        {{ option.text }}
                                    </button>
                                </div>
                                <div class="search-input-wrapper" v-if="searchBy.selected !== 'member'">
                                    <input 
                                        type="text" 
                                        class="form-control-search" 
                                        v-model="searchBy.criteria" 
                                        placeholder="Search..." 
                                        @keyup.enter="search()" 
                                    />
                                    <button v-if="!isSearchActive" class="btn-search" @click="search()">
                                        <i class="fas fa-search"></i>
                                    </button>
                                    <button v-else class="btn-search" @click="clearSearch()">
                                        <i class="fas fa-times text-danger"></i>
                                    </button>
                                </div>
                            </div>

                            <!-- Date Range Control -->
                            <div class="filter-section">
                                <div class="section-label-tabs">
                                    <span class="label-tab">Created At</span>
                                </div>
                                <div class="date-range-box">
                                    <div class="date-input-group">
                                        <DatePicker v-model="sortBy.date_start" placeholder="Start Date" />
                                        <span class="date-separator">-</span>
                                        <DatePicker v-model="sortBy.date_end" placeholder="End Date" />
                                    </div>
                                    <button class="btn-filter-date" @click="sort()" title="Apply Filter">
                                        <i class="fas fa-filter"></i>
                                    </button>
                                </div>
                            </div>

                            <!-- Member Detail Toggle (Shared Logic) -->
                            <o-modal v-if="isMobile && memberSelected" @close="memberSelected = false">
                                <template #header>
                                    <span class="close" @click="memberSelected = false">&times;</span>
                                </template>
                                <template #body>
                                    <o-member-detail
                                        :members="members"
                                        :memberTitle="false"
                                        @update:member_id="handleSelectedMember"
                                    />
                                </template>
                            </o-modal>
                            <o-member-detail
                                v-if="!isMobile && memberSelected"
                                :members="members"
                                :memberTitle="false"
                                @update:member_id="handleSelectedMember"
                            />
                        </div>

                        <div class="action-section">
                            <div class="action-buttons-group">
                                <button class="btn btn-refresh" @click.prevent="refreshAll()" title="Refresh">
                                    <i class="fas fa-sync-alt"></i>
                                </button>
                                <button v-if="this.write" class="btn btn-pending-point" @click.prevent="showPendingPoint = true">
                                    <i class="fas fa-clock me-1"></i> Pending Points
                                </button>
                                <button class="btn btn-export" @click.prevent="download_state()">
                                    <i class="fas fa-file-export me-1"></i> Export
                                </button>
                                <router-link v-if="this.write && isProductModuleActive" :to="{ name: 'TransactionForm' }" class="btn btn-add">
                                    <i class="fas fa-plus me-1"></i> Add New
                                </router-link>
                            </div>
                        </div>
                    </div>
                    <o-table style="text-align:left;" class="table-hover transaction-list-table">
                        <thead>
                            <tr>
                                <th class="col-no">{{ fields[0] }}</th>
                                <th class="col-date">{{ fields[1] }}</th>
                                <th class="col-member">{{ fields[2] }}</th>
                                <th class="col-product">{{ fields[3] }}</th>
                                <th class="col-info">{{ fields[4] }}</th>
                                <th class="col-code">{{ fields[5] }}</th>
                                <th class="col-total">{{ fields[6] }}</th>
                                <th class="col-point">{{ fields[7] }}</th>
                                <th class="col-action text-center">{{ fields[8] }}</th>
                            </tr>
                        </thead>
                        <tbody>
                             <tr v-for="(item, index) in transactions" :key="index" @click="goToEdit(item.id)" class="transaction-row-clickable">
                                <td>{{ (meta.page - 1) * meta.limit + index + 1 }}</td>
                                <td>
                                    <span class="date-text text-nowrap">{{ dateTimeFormat(item.created_at) }}</span>
                                </td>
                                <td>
                                    <div class="d-flex align-items-center gap-2">
                                        <div class="member-avatar theme-bg" v-if="item.member">
                                            <img v-if="item.member.user?.user_profile?.avatar?.image_url && !item.member.imageError" 
                                                 :src="item.member.user.user_profile.avatar.image_url" 
                                                 @error="item.member.imageError = true"
                                                 alt="avatar">
                                            <span v-else class="text-white">{{ getInitials(item.member) }}</span>
                                        </div>
                                        <div class="member-info-text text-truncate">
                                            <span :class="{ 'text-red': !item.member }" class="fw-medium">
                                                {{ item.member ? item.member.title + ' ' + item.member.first_name + ' ' + item.member.last_name : 'Member Deleted' }}
                                            </span>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <div class="product-name-container" :title="item.transaction_detail && item.transaction_detail.length === 1 ? item.transaction_detail[0].name : 'Multiple Products'">
                                        <span class="product-name-clamp">
                                            <template v-if="item.transaction_detail && item.transaction_detail.length > 1">
                                                Multiple Products
                                            </template>
                                            <template v-else-if="item.transaction_detail && item.transaction_detail.length === 1">
                                                {{ item.transaction_detail[0].name }}
                                            </template>
                                        </span>
                                    </div>
                                </td>
                                <td>
                                    <div class="d-flex flex-column gap-1 info-badges-container">
                                        <div class="d-flex">
                                            <span class="source-badge" :class="getSourceClass(item)">
                                                {{ getSourceLabel(item) }}
                                            </span>
                                        </div>
                                        <div class="d-flex gap-1">
                                            <span class="status-badge" :class="getPointBaseClass(item)">
                                                {{ getPointBaseLabel(item) }}
                                            </span>
                                            <span v-if="item.is_expired" class="status-badge expired">Expired</span>
                                            <span v-else class="status-badge active">Active</span>
                                        </div>
                                        <div v-if="item.note" class="d-flex align-items-center gap-1 mt-1">
                                            <span class="note-preview-badge" :title="item.note">
                                                <i class="fas fa-sticky-note" style="font-size:10px;"></i>
                                                <span class="note-preview-text">{{ item.note }}</span>
                                            </span>
                                        </div>
                                    </div>
                                </td>
                                <td><span class="text-muted small">{{ item.code }}</span></td>
                                <td>
                                    <div class="d-flex align-items-center gap-1 justify-content-start">
                                        <div class="info-tooltip-wrapper" :title="`Eligible Amount: ${formatPrice(getPointCalculationAmount(item))}`">
                                            <i class="fas fa-info-circle text-muted" style="cursor: pointer; font-size: 13px;"></i>
                                        </div>
                                        <span class="fw-bold">{{ formatPrice(getGrandtotal(item)) }}</span>
                                    </div>
                                </td>
                                <td>
                                    <template v-if="item.point === null">
                                        <o-loading-small></o-loading-small>
                                    </template>
                                    <template v-else>
                                        <div class="d-flex align-items-center gap-1">
                                            <i class="fas fa-coins text-theme"></i>
                                            <span class="point-value fw-bold text-theme">{{ item.point }}</span>
                                        </div>
                                    </template>
                                </td>
                                <td class="ml-3">
                                    <div class="d-flex gap-2">
                                        <div v-if="this.write">
                                            <router-link 
                                                class="btn btn-action btn-edit"
                                                :to="{ name: 'TransactionEdit', params: { id: item.id } }">
                                                <i class="fas fa-edit"></i>
                                            </router-link>
                                        </div>
                                        <div>
                                            <a href="#" 
                                                class="btn btn-action btn-delete"
                                                @click.prevent.stop="confirmDelete(item)">
                                                <i class="fas fa-trash-alt"></i>
                                            </a>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="transactions.length === 0">
                                <td colspan="10" class="text-center">Empty</td>
                            </tr>
                        </tbody>
                    </o-table>
                </div>
            </div>
            <!-- <input type="text" name="" id="" v-model="navPage"> -->

            <div class="total-summary mt-4 mb-3">
                <div class="row g-3">
                    <div :class="isPointSystemActive ? 'col-md-4' : 'col-md-6'">
                        <div class="summary-card">
                            <div class="summary-content">
                                <div class="summary-icon">
                                    <i class="fas fa-receipt"></i>
                                </div>
                                <div class="summary-details">
                                    <p class="summary-label">Total Transactions</p>
                                    <h3 class="summary-value">{{ totalRows }}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="isPointSystemActive" class="col-md-4">
                        <div class="summary-card">
                            <div class="summary-content">
                                <div class="summary-icon">
                                    <i class="fas fa-coins"></i>
                                </div>
                                <div class="summary-details">
                                    <p class="summary-label">Total Points</p>
                                    <h3 class="summary-value">{{ formatPrice(totalPoints) }}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div :class="isPointSystemActive ? 'col-md-4' : 'col-md-6'">
                        <div class="summary-card">
                            <div class="summary-content">
                                <div class="summary-icon">
                                    <i class="fas fa-wallet"></i>
                                </div>
                                <div class="summary-details">
                                    <p class="summary-label">Grand Total Amount</p>
                                    <h3 class="summary-value">{{ formatPrice(totalGrandTotal) }}</h3>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="container mt-5">
                <o-pagination :page="meta.page" :total="meta.total" @callback="navPage" />
            </div>
        </div>
    </div>
    <br>
    <o-warning-box
            v-if="isDeleteConfirmVisible"
            title="Delete Transaction"
            confirm-text="Yes, Delete"
            cancel-text="Cancel"
            :is-loading="page.isLoading"
            @confirm="deleteItem"
            @cancel="closeDeleteConfirm"
        >
            <template #message>
                <p style="margin-bottom: 0.25rem;">Are you sure you want to delete transaction <strong>"{{ itemToDelete?.code || 'Unknown Code' }}"</strong>?</p>
                <p style="margin-top: 0;">This action cannot be undone.</p>
            </template>
    </o-warning-box>
    <OPendingPoint
            v-if="showPendingPoint"
            :merchantId="selectMerchant && selectMerchant !== 'all' ? selectMerchant : null"
            :branchId="selectedBusinessProfile.branch_id || null"
            @close="showPendingPoint = false"
            @released="handlePendingReleased"
        />
</template>

<script>
import store from '@/store/'
import {mapState} from 'vuex'
import {OTable, OPagination, OBreadCrumb, OSelect, OMemberDetail, OModal, OLoadingSmall, OWarningBox, OPendingPoint} from '@/components/OmnihControl'
// import axios from 'axios'
import { computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useToast } from '@/composables/useToast'
import { usePointSystem } from '@/composables/pointsystem'
import DatePicker from '@/components/DatePicker.vue'


export default {
    data: function() {
        return {
            meta: computed(() => this.$store.state.Transaction.meta),
            fields: [
                'No',
                'Date',
                'Member',
                'Product',
                'Information',
                'Code',
                'Grand Total',
                'Point',
                'Action',
            ],
            page: {
                isLoading: false,
                output: false,
                component: 'transaction'
            },
            grandTotal: [],
            selectMerchant: '',
            selectedBusinessProfile: {
                corporate_id: '',
                branch_id: '',
                merchant_id: ''
            },
            memberSearch: [],
            selectMember: [],
            searchBy: {
                selected: "code",
                criteria: ""
            },
            sortBy: {
                selected: "created_at",
                order: "desc",
                date_start: "",
                date_end: ""
            },
            searchOptions: [
                { value: 'code', text: 'Code' },
                { value: 'member', text: 'Member' },
                { value: 'product', text: 'Product' },
            ],
            sortOptions: [
                { value: 'created_at', text: 'Created at' },
            ],
            validation: '',
            thisComponentShow: true,
            write: false,
            selfLoaded: store.state.Auth.selfLoaded,
            searchBar: true,
            memberSelected: false,
            members: [],
            isMobile: window.innerWidth <= 768,
            lastActivePage: 1,
            isExporting: false,
            pollTimer: null,
            pollCount: 0,
            isDeleteConfirmVisible: false,
            itemToDelete: null,
            isSearchActive: false,
            showPendingPoint: false,
        }
    },
    setup() {
        const route = useRoute()
        const router = useRouter()
        const { toasts, showToast, removeToast } = useToast()
        const { isPointSystemActive } = usePointSystem()

        return {
            route,
            router,
            toasts,
            showToast,
            removeToast,
            isPointSystemActive
        }
    },
    computed: {
        ...mapState({
            transactions: state => state.Transaction.transactions,
            totalData: state => state.Transaction.totalData,
        }),
        totalRows() {            
            return this.totalData.total_data;
        },
        totalPoints() {
            return this.totalData.total_point;
        },
        totalGrandTotal() {
            return this.totalData.total_grand_total;
        },
        isProductModuleActive() {
            const branchProductFlag = store.getters['FeatureFlag/getBranchProductManagementFlag'];
            const merchantProductFlag = store.getters['FeatureFlag/getMerchantProductFlag'];
            return branchProductFlag === 'ACTIVE' || merchantProductFlag === 'ACTIVE';
        },
    },
    watch: {
        selectMerchant(newVal) {
            
            const filter = newVal == "all" ? { branch_id: this.selectedBusinessProfile.branch_id } : { merchant_id: newVal }

            store.commit('Transaction/add_filter', { filter });
            store.dispatch('Transaction/read').then()
                .catch((err) => {
                    // Use the error field from the updated store response
                    if (err.error) {
                        this.validation = err.error;
                    } else {
                        this.validation = "Something went wrong. Please try again.";
                    }
                    this.thisComponentShow = false;
                    // console.error(err);
                })
                .finally(() => this.page.output = true);
        },
        'store.state.Auth.selfLoaded'(newVal) {
            if (newVal) {
                this.selfLoaded = newVal;
                this.load_state();
            }
        }
    },
    components: {
        OTable,
        OPagination,
        OBreadCrumb,
        OSelect,
        OMemberDetail,
        OModal,
        OLoadingSmall,
        DatePicker,
        OWarningBox,
        OPendingPoint,
    },
    mounted() {
        window.addEventListener('resize', this.checkMobile);

        this.$store.commit('Transaction/reset_meta');

        const applyTimeframe = (tf) => {
            if (!tf) return;
            const curr = new Date();
            let startDt = null, endDt = null;
            
            if (tf === 'this_week') {
                const day = curr.getDay() || 7; 
                startDt = new Date(curr);
                startDt.setDate(startDt.getDate() - day + 1);
                endDt = new Date(startDt);
                endDt.setDate(endDt.getDate() + 6);
            } else if (tf === 'this_month') {
                startDt = new Date(curr.getFullYear(), curr.getMonth(), 1);
                endDt = new Date(curr.getFullYear(), curr.getMonth() + 1, 0);
            } else if (tf === 'this_year') {
                startDt = new Date(curr.getFullYear(), 0, 1);
                endDt = new Date(curr.getFullYear(), 11, 31);
            } else if (tf === 'today') {
                startDt = new Date(curr);
                endDt = new Date(curr);
            }
            
            if (startDt && endDt) {
                const formatDt = (d) => {
                    const y = d.getFullYear();
                    const m = String(d.getMonth() + 1).padStart(2, '0');
                    const day = String(d.getDate()).padStart(2, '0');
                    return `${y}-${m}-${day}`;
                };
                this.sortBy.date_start = formatDt(startDt);
                this.sortBy.date_end = formatDt(endDt);
                
                let sort = { created_at: `${this.sortBy.date_start},${this.sortBy.date_end}` };
                this.$store.commit('Transaction/set_sort', {sort});
            }
        };

        // Handle timeframe query parameter from dashboard initially
        applyTimeframe(this.$route.query.timeframe);

        // Also watch for query changes in case the component is reused
        this.$watch(
            () => this.$route.query.timeframe,
            (newTf) => { applyTimeframe(newTf); }
        );

        this.loadSelectedBusinessProfile();

        if (this.selfLoaded) {
            this.load_state();
        }

        if (this.$route.query.toastMessage) {
            const { toastType, toastTitle, toastMessage } = this.$route.query;
            
            // Add slight delay to ensure UI is ready
            setTimeout(() => {
                this.showToast({
                    type: toastType || 'success',
                    title: toastTitle || 'Notification',
                    message: toastMessage
                });
            }, 300);

            // Clear query params without reloading
            this.$router.replace({ query: null });
        }
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.checkMobile);
        clearInterval(this.pollTimer);

        const updatedMeta = { ...this.meta, page: this.lastActivePage };
        this.$store.commit('Transaction/switch_page', { meta: updatedMeta });

        this.$store.commit('Transaction/clear_search');
        this.$store.dispatch('Transaction/read').then(() => {});
    },
    methods: {
        checkMobile() {
            this.isMobile = window.innerWidth <= 768;
        },
        load_state() {
            this.page.isLoading = true;

            if(this.selectMerchant !== 'all' && !this.selectedBusinessProfile.branch_id && !this.selectMerchant) {
                return;
            }

            const filter = this.selectMerchant == "all" 
                ? { branch_id: this.selectedBusinessProfile.branch_id } 
                : { merchant_id: this.selectMerchant };

            store.commit('Transaction/add_filter', { filter });
            store.dispatch('Transaction/read')
                .then(function () {
                })
                .catch((err) => {
                    if (err && err.error) {
                        this.validation = err.error;
                    } else {
                        this.validation = "Something went wrong. Please try again.";
                    }
                    this.thisComponentShow = false;
                    // console.error(err);
                })
                .finally(() => {
                    const hasPending = this.transactions.some(t => t.point === null);
                    if (hasPending) {
                        if (!this.pollTimer) {
                            this.startPolling();
                        }
                    } else {
                        clearInterval(this.pollTimer);
                        this.pollTimer = null;
                        this.pollCount = 0;
                    }
                    this.page.isLoading = false;
                    this.page.output = true;

                    if (store.state.Auth.currUser.level === 'root') {
                        this.write = true;
                    } else {
                        const userModules = store.state.Auth.userInfo.modules;

                        if (userModules) {
                            const matchingModule = userModules.find(module => module.module_name === this.page.component);
                            this.write = matchingModule ? matchingModule.write : false;
                        } else {
                            // console.error('userModules is undefined');
                            this.write = false;
                        }
                    }
                });
        },
        goToEdit(id) {
            if (this.write) {
                this.$router.push({ name: 'TransactionEdit', params: { id: id } });
            }
        },
        getInitials(member) {
            if (!member || !member.first_name) return 'M';
            let initials = member.first_name.charAt(0);
            if (member.last_name) {
                initials += member.last_name.charAt(0);
            }
            return initials.toUpperCase();
        },
        loadSelectedBusinessProfile() {
            store.dispatch('Auth/getBusinessProfile').then((businessProfile) => {
                this.selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
                this.selectedBusinessProfile.branch_id = businessProfile.branch_id;
                this.selectedBusinessProfile.merchant_id = businessProfile.merchant_id;
                this.selectMerchant = !businessProfile.merchant_id ? "all" : businessProfile.merchant_id;

                if (!businessProfile.merchant_id) {
                    this.loadMerchants();
                }

                if (businessProfile.corporate_id) {
                    store.dispatch('FeatureFlag/fetchFeatureFlag', businessProfile.corporate_id);
                }
            }).finally(() => {
                this.load_state();
            });
        },
        confirmDelete(item) {
            this.itemToDelete = item;
            this.isDeleteConfirmVisible = true;
        },
        deleteItem() {
            if (!this.itemToDelete) return;
            
            this.page.isLoading = true;
            const transactionCode = this.itemToDelete.code;
            
            store.dispatch('Transaction/delete', {
                transactionId: this.itemToDelete.id
            })
            .then(() => {
                this.showToast({
                    type: 'success',
                    title: 'Deleted',
                    message: `Transaction ${transactionCode} has been successfully deleted.`
                });
                this.closeDeleteConfirm();
            })
            .catch((err) => {
                this.handleError(err);
                this.showToast({
                    type: 'error',
                    title: 'Delete Failed',
                    message: this.validation || 'Failed to delete transaction.'
                });
                this.closeDeleteConfirm();
            })
            .finally(() => {
                this.page.isLoading = false;
            });
        },
        closeDeleteConfirm() {
            this.isDeleteConfirmVisible = false;
            this.itemToDelete = null;
        },
        /**
         * Get grand total products (sum of subtotals)
         */
        getGrandTotalProducts(transaction) {
            if (!transaction || !transaction.transaction_detail) return 0;
            return transaction.transaction_detail.reduce((acc, item) => {
                const subtotal = parseFloat(item.subtotal);
                return acc + (isNaN(subtotal) ? 0 : subtotal);
            }, 0);
        },
        /**
         * Get the amount used for point calculation based on transaction type and settings
         * This shows the exact amount that was used to calculate points, eliminating confusion
         */
        getPointCalculationAmount(transaction) {
            if (!transaction) return 0;
            
            // 0 = Before Tax (Subtotal), 1 = After Tax (Grand Total)
            if (transaction.before_after_tax_service == 1) {
                return (transaction.after_tax_service !== null && transaction.after_tax_service !== undefined)
                    ? parseFloat(transaction.after_tax_service) 
                    : this.getGrandtotal(transaction);
            } else {
                return (transaction.before_tax_service !== null && transaction.before_tax_service !== undefined)
                    ? parseFloat(transaction.before_tax_service) 
                    : this.getSubtotal(transaction);
            }
        },
        /**
         * Get transaction source label based on is_nett_price field
         */
        getSourceLabel(transaction) {
            if (transaction.is_nett_price === 'nett') return 'External (Nett)';
            if (transaction.is_nett_price === 'taxservice') return 'External (Gross)';
            return 'Membership Apps';
        },
        /**
         * Get CSS class for source badge
         */
        getSourceClass(transaction) {
            if (transaction.is_nett_price === 'nett') return 'external-nett';
            if (transaction.is_nett_price === 'taxservice') return 'external-gross';
            return 'internal';
        },
        /**
         * Get point calculation base label from stored values.
         * Determines if points were calculated from "Before Tax" or "After Tax" amount.
         */
        getPointBaseLabel(transaction) {
            if (!transaction) return 'Before';
            if (transaction.before_after_tax_service == 1) {
                return 'After';
            }
            return 'Before';
        },
        /**
         * Get CSS class for point base badge
         */
        getPointBaseClass(transaction) {
            return this.getPointBaseLabel(transaction) === 'After' ? 'after-tax' : 'before-tax';
        },
        /**
         * Get subtotal (before tax & service)
         */
        getSubtotal(transaction) {
            if (!transaction || !transaction.transaction_detail) return 0;
            // Use before_tax_service if available
            if (transaction.before_tax_service !== null && transaction.before_tax_service !== undefined) {
                return parseFloat(transaction.before_tax_service);
            }
            // Fallback to sum of detail subtotals
            return transaction.transaction_detail.reduce((acc, item) => {
                const subtotal = parseFloat(item.subtotal);
                return acc + (isNaN(subtotal) ? 0 : subtotal);
            }, 0);
        },
        /**
         * Get grandtotal (after tax & service)
         */
        getGrandtotal(transaction) {
            if (!transaction) return 0;
            // Use after_tax_service if available (stored by backend as final grand total)
            if (transaction.after_tax_service !== null && transaction.after_tax_service !== undefined) {
                return parseFloat(transaction.after_tax_service);
            }
            // Use amount_after_voucher if available
            if (transaction.amount_after_voucher !== null && transaction.amount_after_voucher !== undefined) {
                return parseFloat(transaction.amount_after_voucher);
            }
            // Fallback to totalAmount calculation
            return this.totalAmount(transaction);
        },
        totalAmount: function (transaction) {
            if (!transaction || !transaction.transaction_detail) {
                return 0; 
            }

            // Use after_tax_service if available (stored by backend as final grand total)
            if (transaction.after_tax_service !== null && transaction.after_tax_service !== undefined) {
                return parseFloat(transaction.after_tax_service);
            }

            // Use amount_after_voucher if available
            if (transaction.amount_after_voucher !== null && transaction.amount_after_voucher !== undefined) {
                return parseFloat(transaction.amount_after_voucher);
            }

            return transaction.transaction_detail.reduce((acc, item) => {
                const subtotal = parseFloat(item.subtotal);
                return acc + (isNaN(subtotal) ? 0 : subtotal);
            }, 0);
        },

        handleError(err) {
            // Use the error field from the updated store response
            if (err.error) {
                this.validation = err.error;
            } else if (err.response && err.response.data && err.response.data.error) {
                this.validation = err.response.data.error;
            } else if (err.message && err.status == false) {
                this.validation = err.message;
            } else {
                this.validation = "Something went wrong";
            }
        },
        
        formatPrice: function (value) {
            if (value === null || value === undefined || value === '') return '0';
            
            // If value is already a string, it might contain separators like commas or dots
            let numericValue = value;
            if (typeof value === 'string') {
                // Remove any existing thousands separators (commas or dots)
                // We assume these are thousands separators if the backend sends pre-formatted strings
                numericValue = value.replace(/[,.]/g, '');
            }
            
            const val = parseFloat(numericValue);
            if (isNaN(val)) return value;

            // Format with dot as thousands separator
            return val.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        },
        
        navPage: function (page){
            const _meta = {... this.meta, page}

            store.commit('Transaction/switch_page',{meta:_meta})
            this.load_state()
        },

        loadMerchants: function () {
            const filter = {
                branch_id: this.selectedBusinessProfile.branch_id,
            }

            store.commit('Merchant/add_filter', {filter});
            store.dispatch('Merchant/read').then(() => {
                this.merchants = [
                    {value: "all", text: "From all merchant"},
                    ...store.state.Merchant.merchants.map((merch)=>({'value':merch.id,'text':merch.name}))
                    ]
            })
        },

        search: function (searchBy) {
            // Support calling without arguments (uses local state)
            const query = (searchBy && searchBy.selected) ? searchBy : this.searchBy;
            const {selected, criteria} = query;

            if (selected && criteria) {
                let search = {}
                search[selected] = criteria;

                const updatedMeta = { ...this.meta, page: 1 };
                store.commit('Transaction/switch_page', { meta: updatedMeta });
                store.commit('Transaction/set_search', {search});
                this.isSearchActive = !!this.searchBy.criteria;
                this.applyFilters();
            }
        },

        sort: function (sortBy) {
            // Support calling without arguments (uses local state)
            const params = (sortBy && sortBy.selected) ? sortBy : this.sortBy;
            const {selected, order, date_start, date_end} = params;

            if (selected) {
                let sort = {};
                
                sort[selected] = order;

                if (selected === 'created_at' && date_start && date_end) {
                    sort[selected] = `${date_start},${date_end}`
                }

                // this.sortBy = sort;
                const updatedMeta = { ...this.meta, page: 1 };
                store.commit('Transaction/switch_page', { meta: updatedMeta });
                
                store.commit('Transaction/set_sort', {sort});
                this.applyFilters();
            }
        },

        selectSearchType(type) {
            this.searchBy.selected = type;
            this.searchBy.criteria = ''; // Clear input
            this.handleSelectedChanged(type);
        },

        applyFilters: function() {
            store.dispatch('Transaction/read').then(() => {});
        },

        clearSearch: function(){
            const updatedMeta = { ...this.meta, page: this.lastActivePage };
            store.commit('Transaction/switch_page', { meta: updatedMeta });
            store.commit('Transaction/clear_search');
            this.searchBy.criteria = '';
            this.isSearchActive = false;
            this.applyFilters();
        },

        clearSort: function(){
            this.sortBy = {
                selected: "created_at",
                order: "desc"
            };
            store.commit('Transaction/clear_sort');
            this.applyFilters();
        },
        
        refreshAll: function() {
            this.searchBy.criteria = '';
            this.isSearchActive = false;
            store.commit('Transaction/clear_search');
            
            this.sortBy = {
                selected: "created_at",
                order: "desc",
                date_start: "",
                date_end: ""
            };
            store.commit('Transaction/clear_sort');
            
            this.memberSelected = false;
            
            if (this.selectedBusinessProfile.merchant_id == null && this.selectMerchant !== 'all') {
                this.selectMerchant = 'all';
            } else {
                this.load_state();
            }
        },

        handlePendingReleased() {
            this.load_state();
        },

        async download_state() {
            this.isExporting = true;

            try {
                const response = await store.dispatch('Transaction/export');
                await this.handleDownloadResponse(response);
            } catch (err) {
                // console.error(err);
            } finally {
                this.isExporting = false;
            }
        },
        async handleDownloadResponse(response) {
            const fileUrl = response.data.data; // URL dari response
            const fileName = fileUrl.split('/').pop(); // Ambil nama file dari URL

            // Buat elemen <a> untuk memulai download
            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', fileName); // Set nama file untuk download
            document.body.appendChild(link);
            link.click(); // Trigger klik untuk memulai download
            document.body.removeChild(link); // Hapus elemen setelah selesai
        },
        handleSelectedChanged(newSelected) {
            this.clearSearch();
            
            if (newSelected === 'member') {
                this.memberSelected = true;
                this.searchBar = false;
            } else {
                this.memberSelected = false;
                this.searchBar = true;
            }
        },
        handleSelectedMember(memberId) {
            let search = {}

            if (memberId != null) {
                search['member_id'] = memberId;

                store.commit('Transaction/set_search', {search});
                store.dispatch('Transaction/read').then(() => {})
                .catch((err) => {
                    if (err && err.response && err.response.data && typeof err.response.data.message === 'string' && err.response.data.message.trim() !== '') {
                        this.validation = err.response.data.message;
                    } else {
                        this.validation = "Something went wrong. Please try again.";
                    }
                    this.thisComponentShow = false;
                    // console.error(err);
                })
                .finally(() => this.page.output = true);
            } else {
                this.clearSearch();
            }  
        },
        startPolling() {
            this.pollCount = 0;
            this.pollTimer = setInterval(() => {
                this.pollCount++;
                if (this.pollCount >= 12) {
                    clearInterval(this.pollTimer);
                    this.pollTimer = null;
                    return;
                }
                this.load_state();
            }, 15000);
        },
        dateTimeFormat(date) {
            if (!date) return '-';
            const dateObj = new Date(date);
            return dateObj.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            }) + ' ' + dateObj.toLocaleTimeString('en-GB', {
                hour: '2-digit',
                minute: '2-digit'
            });
        }
    }
}
</script>

<style scoped>

/* Note preview badge in list */
.note-preview-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    color: #0369a1;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 4px;
    max-width: 160px;
    cursor: default;
}
.note-preview-text {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    max-width: 130px;
}

.form-group {
    width: 250px;
    text-align: left;
    padding-left: 16px;
}
.text-red {
    color: var(--primary-color);
}
.parent-flex-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}
.button-container {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
}
.exporting-animation {
    display: inline-flex;
    gap: 2px;
}

.fade-char {
    display: inline-block;
    opacity: 0;
    animation: fade 2s infinite;
    animation-delay: calc(var(--i) * 0.2s);
}

@keyframes fade {
    0%, 100% {
        opacity: 0;
    }
    50% {
        opacity: 1;
    }
}

.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* Summary Cards Styling */
.total-summary {
    padding: 0 10px;
}

.summary-card {
    background: white;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    transition: all 0.2s ease;
    height: 100%;
}

.summary-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.summary-content {
    display: flex;
    align-items: center;
    gap: 16px;
}

.summary-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background-color: var(--primary-color);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 20px;
    flex-shrink: 0;
}

.summary-details {
    flex: 1;
    min-width: 0;
}

.summary-label {
    font-size: 11px;
    font-weight: 600;
    color: #6c757d;
    margin: 0 0 2px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.summary-value {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
    line-height: 1.2;
    color: #212529;
}

/* Table Styling Improvements */
.transaction-list-table {
    white-space: nowrap;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
}

.transaction-list-table th,
.transaction-list-table td {
    padding: 12px 16px !important;
    vertical-align: middle;
}

.date-text {
    font-size: 13px;
    color: #4a5568;
    white-space: nowrap;
}

/* Source and Point Base Badges */
.source-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    display: inline-block;
}

.source-badge.external-nett {
    background-color: #ebf5ff;
    color: #004e9a;
}

.source-badge.external-gross {
    background-color: #fff1f2;
    color: #9f1239;
}

.source-badge.internal {
    background-color: #f0fdf4;
    color: #166534;
}

.point-base-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    display: inline-block;
}

.point-base-badge.before-tax {
    background-color: #f7fee7;
    color: #4d7c0f;
}

.point-base-badge.after-tax {
    background-color: #fefce8;
    color: #854d0e;
}

.point-value {
    font-weight: 700;
    color: var(--primary-color);
    font-size: 15px;
}

/* Action Buttons */
.btn-action {
    padding: 6px 14px;
    font-size: 12px;
    font-weight: 600;
    border-radius: 8px;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
    border: none;
}

.btn-edit {
    background-color: #f3f4f6;
    color: #374151;
}

.btn-edit:hover {
    background-color: #e5e7eb;
    color: #111827;
}

.btn-delete {
    background-color: #fff1f2;
    color: #9f1239;
}

.btn-delete:hover {
    background-color: #ffe4e6;
    color: #be123c;
}

/* Responsive Design */
@media (max-width: 768px) {
    .summary-card {
        padding: 16px;
    }
    
    .summary-content {
        gap: 12px;
    }
    
    .summary-icon {
        width: 40px;
        height: 40px;
        font-size: 18px;
    }
    
    .summary-value {
        font-size: 20px;
    }
    
    .summary-label {
        font-size: 12px;
    }
}

@media (max-width: 576px) {
    .total-summary {
        padding: 0 5px;
    }
    
    .summary-card {
        padding: 12px;
    }
    
    .summary-content {
        gap: 10px;
    }
    
    .summary-icon {
        width: 36px;
        height: 36px;
        font-size: 16px;
    }
    
    .summary-value {
        font-size: 18px;
    }
}
/* Filter and Action Bar Styling */
.filter-action-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: flex-end; /* Align to bottom for balance with headers */
    gap: 20px;
    flex-wrap: wrap;
    background-color: #f8fafc;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #e2e8f0;
}

.filter-section, .action-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.section-label-tabs {
    display: flex;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.label-tab {
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color);
    background: white;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.filter-controls {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    flex-wrap: wrap;
    flex: 1;
}

.merchant-filter {
    min-width: 200px;
}

/* Custom Search Box */
.search-box-custom {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 0 1 350px; /* Flex value: grow 0, shrink 1, basis 350px */
    min-width: 250px;
}

.search-type-tabs {
    display: flex;
    gap: 4px;
    background: #f1f5f9;
    padding: 4px;
    border-radius: 8px;
    width: fit-content;
}

.tab-btn {
    border: none;
    background: transparent;
    padding: 6px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #64748b;
    border-radius: 6px;
    transition: all 0.2s;
    cursor: pointer;
}

.tab-btn.active {
    background: white;
    color: var(--primary-color);
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    font-weight: 600;
}

.search-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    position: relative;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 4px;
    transition: all 0.2s;
}

.search-input-wrapper:focus-within {
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-rgb), 0.1);
}

.search-input-wrapper input {
    border: none;
    padding: 8px 12px;
    font-size: 14px;
    width: 100%;
    outline: none;
    background: transparent;
    color: #334155;
}

.btn-search {
    background: #f8fafc;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 6px;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-search:hover {
    background: #e2e8f0;
    color: var(--primary-color);
}

/* Date Range Box */
.date-range-box {
    display: flex;
    align-items: center;
    gap: 8px;
}

.date-input-group {
    display: flex;
    align-items: center;
    gap: 8px;
}

.date-input-group > div {
    min-width: 140px;
}

.date-separator {
    color: #94a3b8;
    font-weight: 500;
}

.btn-filter-date {
    background: var(--primary-color);
    color: white;
    border: none;
    width: 38px;
    height: 38px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-filter-date:hover {
    filter: brightness(0.9);
}

.action-buttons-group {
    display: flex;
    align-items: center;
    gap: 10px;
}

.btn-refresh {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #64748b;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.btn-refresh:hover {
    background-color: #f1f5f9;
    color: #1e293b;
    border-color: #cbd5e1;
}

.btn-export {
    background-color: #fff;
    border: 1px solid #e2e8f0;
    color: #475569;
    font-weight: 600;
    font-size: 13px;
    padding: 0 16px;
    height: 40px;
    display: flex;
    align-items: center;
    transition: all 0.2s;
}

.btn-export:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1;
}

.btn-pending-point {
    background-color: #fffbeb;
    border: 1px solid #fde68a;
    color: #92400e;
    font-weight: 600;
    font-size: 13px;
    padding: 0 16px;
    height: 40px;
    display: flex;
    align-items: center;
    transition: all 0.2s;
}

.btn-pending-point:hover {
    background-color: #fef3c7;
    border-color: #fcd34d;
    color: #78350f;
}

.btn-add {
    background-color: var(--primary-color);
    color: white;
    font-weight: 600;
    font-size: 13px;
    padding: 0 20px;
    height: 40px;
    display: flex;
    align-items: center;
    border: none;
    transition: all 0.2s;
}

.btn-add:hover {
    filter: brightness(0.9);
    color: white;
}

/* Clickable Row */
.transaction-row-clickable {
    cursor: pointer;
    transition: background-color 0.2s;
}

.transaction-row-clickable:hover {
    background-color: #f8fafc !important;
}

/* Responsive Overrides */
.header-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 0.25rem 0;
    display: flex;
    align-items: center;
}

.header-subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}

/* Responsive Overrides */
@media (max-width: 991px) {
    .filter-controls {
        flex: 100%;
        flex-direction: column;
        align-items: stretch;
    }
    
    .merchant-filter, .search-box-custom {
        min-width: 100%;
    }

    .date-range-box {
        border-left: none;
        padding-left: 0;
        margin-top: 12px;
        flex-direction: row;
        width: 100%;
    }

    .date-input-group {
        flex: 1;
    }

    .date-input-group > div {
        flex: 1;
    }

    .action-buttons-group {
        flex: 100%;
        justify-content: space-between;
        margin-top: 12px;
    }
    
    .btn-add {
        flex: 1;
        justify-content: center;
    }
}

/* Status Badges */
.status-badge {
    padding: 2px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: inline-block;
}

.status-badge.active {
    background-color: #e0f2fe;
    color: #0369a1;
    border: 1px solid #bae6fd;
}

.status-badge.expired {
    background-color: #fff1f2;
    color: #be123c;
    border: 1px solid #fecdd3;
}
/* Minimalist Action Buttons Style */
.btn-action {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    transition: all 0.2s ease;
    border: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.btn-edit {
    background-color: #f1f5f9;
    color: #475569;
}

.btn-edit:hover {
    background-color: #e2e8f0;
    color: #334155;
}

.btn-delete {
    background-color: #f87171; /* Matching salmon/coral theme of Add New */
    color: #ffffff;
}

.btn-delete:hover {
    background-color: #ef4444;
    color: #ffffff;
}

/* Base Badge Alignment Fix */
.info-tooltip-wrapper {
    display: flex;
    align-items: center;
    color: #94a3b8;
    transition: color 0.2s;
}

.info-tooltip-wrapper:hover {
    color: #64748b;
}

/* Refined Minimalist Badges */
.status-badge {
    padding: 2px 8px;
    border-radius: 5px;
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    display: inline-block;
    white-space: nowrap;
    border: none;
}

.status-badge.active {
    background-color: #f0f9ff;
    color: #0c4a6e;
    border: 1px solid #bae6fd;
}

/* Base Badge - Before Tax (Amber/Orange) */
.status-badge.before-tax {
    background-color: #fffbeb;
    color: #92400e;
    border: 1px solid #fef3c7;
}

/* Base Badge - After Tax (Indigo/Purple) */
.status-badge.after-tax {
    background-color: #f5f3ff;
    color: #4338ca;
    border: 1px solid #ddd6fe;
}

/* Source Badges */
.source-badge {
    padding: 2px 8px;
    border-radius: 5px;
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.2px;
    display: inline-block;
    white-space: nowrap;
}

.source-badge.internal {
    background-color: #f0fdf4;
    color: #166534;
    border: 1px solid #bbf7d0;
}

.source-badge.external-nett,
.source-badge.external-gross {
    background-color: #f8fafc;
    color: #475569;
    border: 1px solid #e2e8f0;
}

.status-badge.expired {
    background-color: #fff1f2;
    color: #9f1239;
    border: 1px solid #fecdd3;
}
/* Information Cluster (Badges) */
.info-badges-container {
    max-width: 280px;
}

.status-badge {
    padding: 3px 6px;
    border-radius: 4px;
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    display: inline-block;
    border: 1px solid transparent;
}

.source-badge {
    padding: 3px 6px;
    border-radius: 4px;
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    display: inline-block;
    border: 1px solid transparent;
}

.text-theme {
    color: #f87171 !important;
}

.small {
    font-size: 0.8rem;
}

/* Table Column Width Ethics */
.col-no, .col-date, .col-point, .col-total, .col-code, .col-info {
    width: 1%;
    white-space: nowrap;
}

.col-member {
    min-width: 120px;
    max-width: 150px;
}

.col-product {
    min-width: 110px;
    max-width: 130px;
}

.col-info {
    min-width: 120px;
}

.col-action {
    width: 80px;
}

.member-info-text {
    max-width: 120px;
}

.product-name-container {
    max-width: 130px;
}

.product-name-clamp {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    line-height: 1.4;
    font-size: 13px;
    color: #334155;
    font-weight: 500;
}

/* Member Avatar */
.member-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #475569;
    font-weight: 700;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
}

.member-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.fw-medium {
    font-weight: 500;
}

.theme-bg {
    background-color: #f87171 !important;
}

.text-white {
    color: #ffffff !important;
}
</style>